import type { View } from "@/types"

// unbounded slider values; a range at these bounds is omitted from a View ("no constraint")
export const RANGE_SLIDER_MAX = 5_000_000_000
export const RESIDENTIAL_UNITS_SLIDER_MIN = 0
export const RESIDENTIAL_UNITS_SLIDER_MAX = 2_000

const DEFAULT_NUMBER_RANGES = {
	landArea: [0, RANGE_SLIDER_MAX],
	buildableLandArea: [0, RANGE_SLIDER_MAX],
	price: [0, RANGE_SLIDER_MAX],
	residentialUnits: [RESIDENTIAL_UNITS_SLIDER_MIN, RESIDENTIAL_UNITS_SLIDER_MAX],
	superficy: [0, RANGE_SLIDER_MAX],
	dwellingUnits: [0, RANGE_SLIDER_MAX],
	storeys: [0, RANGE_SLIDER_MAX],
	propertyValue: [0, RANGE_SLIDER_MAX],
	buildingAge: [0, RANGE_SLIDER_MAX],
} as const

type NumberRangeField = keyof typeof DEFAULT_NUMBER_RANGES

function isDefaultNumberRange(
	field: NumberRangeField,
	range: { min?: number | null; max?: number | null } | undefined
): boolean {
	const [min, max] = DEFAULT_NUMBER_RANGES[field]
	return (range?.min ?? min) === min && (range?.max ?? max) === max
}

function normalizeNumberRanges<
	T extends Partial<Record<NumberRangeField, { min?: number | null; max?: number | null }>>,
>(filter: T): T {
	const normalizedFilter = { ...filter }
	for (const field of Object.keys(DEFAULT_NUMBER_RANGES) as NumberRangeField[]) {
		if (isDefaultNumberRange(field, filter[field])) {
			delete normalizedFilter[field]
		}
	}
	return normalizedFilter
}

export function normalizeViewFilterDefaults(view: View): View {
	const opportunity = view.filter?.opportunity
	const transaction = view.filter?.transaction
	const buyerTransaction = view.filter?.buyer?.transaction
	const parcel = view.filter?.parcel
	if (!opportunity && !transaction && !buyerTransaction && !parcel) return view

	const normalizedOpportunity = opportunity ? normalizeNumberRanges(opportunity) : undefined
	const normalizedTransaction = transaction ? normalizeNumberRanges(transaction) : undefined
	const normalizedBuyerTransaction = buyerTransaction ? normalizeNumberRanges(buyerTransaction) : undefined
	const normalizedParcel = parcel ? normalizeNumberRanges(parcel) : undefined

	return {
		...view,
		filter: {
			...view.filter,
			...(normalizedOpportunity ? { opportunity: normalizedOpportunity } : {}),
			...(normalizedTransaction ? { transaction: normalizedTransaction } : {}),
			...(normalizedParcel ? { parcel: normalizedParcel } : {}),
			...(normalizedBuyerTransaction
				? {
						buyer: {
							...view.filter?.buyer,
							transaction: normalizedBuyerTransaction,
						},
					}
				: {}),
		},
	}
}

function isEmptyFilterValue(value: unknown): boolean {
	if (value == null) return true
	if (Array.isArray(value)) return value.length === 0
	if (typeof value === "object") return Object.values(value).every(isEmptyFilterValue)
	return false
}

export function activeFilterFieldCount(
	filter: Record<string, unknown> | null | undefined,
	excludeFields: string[] = []
): number {
	if (!filter) return 0

	return Object.entries(filter).filter(([key, value]) => {
		if (excludeFields.includes(key)) return false
		if (key in DEFAULT_NUMBER_RANGES) {
			return !isDefaultNumberRange(key as NumberRangeField, value as { min?: number | null; max?: number | null })
		}
		return !isEmptyFilterValue(value)
	}).length
}
